import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FiImage, FiTrash2, FiBarChart2, FiPlus, FiX, FiLoader } from "react-icons/fi";
import { createAnnouncement, getAnnouncementsByMintSpace, toggleAnnouncementLike, deleteAnnouncement, voteOnAnnouncementPoll } from "../api/announcements";
import { uploadImage, uploadVideo, uploadMintSpaceLogo, uploadWelcomeBanner } from "../api/upload";
import { getMintSpaceRating, updateMintSpace } from "../api/mintSpace";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../context/TranslationContext";
import CustomVideoPlayer from "../components/CustomVideoPlayer";
import MintSpaceProductsGrid from "../components/MintSpaceProductsGrid";
import CampaignAnnouncementCard from "../components/CampaignAnnouncementCard";
import CampaignPeopleCard from "../components/CampaignPeopleCard";
import SpaceSocialLinks from "../components/SpaceSocialLinks";
import LinkPreviewCard, { LinkifiedText, findLinks } from "../components/LinkPreviewCard";
import { ReviewsRow } from "../components/discover/DiscoverAll";
import PostComments from "../components/comments/PostComments";
import { refusedUploadMessage } from "../utils/refusedUpload";
import { getBusinessCategoryLabel } from "../constants/businessCategories";
import { getProfileAvatarUrl } from "../utils/userDisplay";
import { getShareUrl, shareOrCopyUrl } from "../utils/share";
import Alert from "../components/ds/Alert";
import Avatar from "../components/ds/Avatar";
import Button from "../components/ds/Button";
import EmptyState from "../components/ds/EmptyState";
import Icon from "../components/ds/Icon";
import IconButton from "../components/ds/IconButton";

const MintSpaceAnnouncementsPage = ({ isDark, setToast, isOwner, mintSpace, type = "announcement", onNavigate, onUpdate, onEditSpace }) => {
  const isPost = type === "post";
  const pageTitle = isPost ? "Home" : "Announcements";
  const emptyLabel = isPost ? "No posts yet. Share something with your community!" : "No announcements yet. Create your first post!";
  const loadingLabel = isPost ? "Loading posts..." : "Loading announcements...";
  const postedLabel = isPost ? "toasts.postCreated" : "toasts.announcementPosted";
  const { t, language } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [highlightedPostId, setHighlightedPostId] = useState(null);
  const handledHighlightRef = useRef(false);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newPost, setNewPost] = useState("");
  const [newPostImage, setNewPostImage] = useState(null);
  const [newPostImagePreview, setNewPostImagePreview] = useState(null);
  const [newPostVideo, setNewPostVideo] = useState(null);
  const [newPostVideoPreview, setNewPostVideoPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  // Poll composer state — when pollDraft is non-null, this post is a poll (media disabled)
  const [pollDraft, setPollDraft] = useState(null); // { question, options: [string, string, ...] }
  // Which posts have their comments open
  const [openComments, setOpenComments] = useState({}); // postId -> bool
  // Logo (cover avatar) editing
  const logoInputRef = useRef(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoOverride, setLogoOverride] = useState(null);
  // Cover banner editing
  const bannerInputRef = useRef(null);
  const [bannerUploading, setBannerUploading] = useState(false);
  const [bannerOverride, setBannerOverride] = useState(null);
  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState(null); // postId pending deletion
  const [isDeleting, setIsDeleting] = useState(false);

  const handleBannerChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setToast(t("windows.imageSizeMustBeLessThan5MB") || "Image size must be less than 5MB");
      e.target.value = "";
      return;
    }
    if (!mintSpace?._id) return;
    const objectUrl = URL.createObjectURL(file);
    setBannerOverride(objectUrl);
    setBannerUploading(true);
    try {
      const uploadResponse = await uploadWelcomeBanner(file);
      if (!uploadResponse.success) throw new Error("Upload failed");
      const result = await updateMintSpace(mintSpace._id, {
        pages: {
          welcome: {
            enabled: mintSpace.pages?.welcome?.enabled ?? true,
            banner: uploadResponse.url,
            content: mintSpace.pages?.welcome?.content || "",
          },
        },
      });
      if (!result.success) throw new Error("Update failed");
      URL.revokeObjectURL(objectUrl);
      setBannerOverride(uploadResponse.url);
      if (onUpdate) onUpdate(result.data);
      setToast(t("mintSpace.imageUpdatedSuccessfully") || "Image updated successfully");
    } catch (error) {
      console.error("Error updating banner:", error);
      URL.revokeObjectURL(objectUrl);
      setBannerOverride(null);
      setToast(refusedUploadMessage(error) || t("mintSpace.errorUpdatingImage") || "Error updating image");
    } finally {
      setBannerUploading(false);
      e.target.value = "";
    }
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setToast(t("windows.imageSizeMustBeLessThan5MB") || "Image size must be less than 5MB");
      e.target.value = "";
      return;
    }
    if (!mintSpace?._id) return;
    const objectUrl = URL.createObjectURL(file);
    setLogoOverride(objectUrl);
    setLogoUploading(true);
    try {
      const uploadResponse = await uploadMintSpaceLogo(file);
      if (!uploadResponse.success) throw new Error("Upload failed");
      const result = await updateMintSpace(mintSpace._id, { logo: uploadResponse.url });
      if (!result.success) throw new Error("Update failed");
      URL.revokeObjectURL(objectUrl);
      setLogoOverride(uploadResponse.url);
      if (onUpdate) onUpdate(result.data);
      setToast(t("mintSpace.imageUpdatedSuccessfully") || "Image updated successfully");
    } catch (error) {
      console.error("Error updating logo:", error);
      URL.revokeObjectURL(objectUrl);
      setLogoOverride(null);
      setToast(refusedUploadMessage(error) || t("mintSpace.errorUpdatingImage") || "Error updating image");
    } finally {
      setLogoUploading(false);
      e.target.value = "";
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setToast(t("actualHome.imageSizeMustBeLessThan10MB"));
        return;
      }
      // Clear video and poll if image is selected
      setNewPostVideo(null);
      setNewPostVideoPreview(null);
      setPollDraft(null);
      setNewPostImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewPostImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 100 * 1024 * 1024) {
        setToast(t("actualHome.videoSizeMustBeLessThan100MB"));
        return;
      }
      // Clear image and poll if video is selected
      setNewPostImage(null);
      setNewPostImagePreview(null);
      setPollDraft(null);
      setNewPostVideo(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewPostVideoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Fetch announcements
  // The space's rating: the average of its products' reviews.
  const [rating, setRating] = useState({ averageRating: null, ratingCount: 0 });
  useEffect(() => {
    if (!isPost || !mintSpace?._id) return;
    getMintSpaceRating(mintSpace._id)
      .then((res) => res?.success && res.data && setRating(res.data))
      .catch(() => {});
  }, [isPost, mintSpace?._id]);

  useEffect(() => {
    const fetchAnnouncements = async () => {
      if (!mintSpace?._id) return;
      
      try {
        setLoading(true);
        // Home shows everything (incl. legacy announcements); Announcements tab (if ever
        // re-added) would pass type="announcement" to filter.
        const fetchType = type === "post" ? undefined : type;
        const response = await getAnnouncementsByMintSpace(mintSpace._id, fetchType);
        if (response.success) {
          setPosts(response.data || []);
        }
      } catch (error) {
        console.error("Error fetching announcements:", error);
        setToast(t("toasts.failedLoadAnnouncements"));
      } finally {
        setLoading(false);
      }
    };

    fetchAnnouncements();
  }, [mintSpace?._id, setToast, t, type]);

  // Auto-scroll + highlight when arriving via a shared post link (?post=<id>)
  useEffect(() => {
    if (handledHighlightRef.current || loading) return;
    const target = searchParams.get("post");
    if (!target) return;
    if (!posts.some((p) => p._id === target)) return;

    handledHighlightRef.current = true;
    setHighlightedPostId(target);

    requestAnimationFrame(() => {
      const el = document.getElementById(`post-${target}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    });

    // Strip ?post= so a refresh/back doesn't re-trigger the highlight
    setSearchParams(
      (prev) => {
        const np = new URLSearchParams(prev);
        np.delete("post");
        return np;
      },
      { replace: true }
    );

    const timer = setTimeout(() => setHighlightedPostId(null), 2500);
    return () => clearTimeout(timer);
  }, [loading, posts, searchParams, setSearchParams]);

  const handlePost = async () => {
    const hasPoll = !!pollDraft;
    const validPollOptions = hasPoll
      ? pollDraft.options.map((o) => o.trim()).filter((o) => o !== "")
      : [];
    const pollValid = hasPoll && pollDraft.question.trim() !== "" && validPollOptions.length >= 2;

    if (hasPoll && !pollValid) {
      setToast("Polls need a question and at least 2 options");
      return;
    }
    if (!hasPoll && !newPost.trim() && !newPostImagePreview && !newPostVideoPreview) {
      setToast(t("toasts.pleaseAddPostContent"));
      return;
    }

    if (!mintSpace?._id) {
      setToast(t("mintSpace.mintSpaceNotFound"));
      return;
    }

    try {
      setUploading(true);
      let imageUrl = "";
      let videoUrl = "";

      // Upload image if present (skipped for polls)
      if (!hasPoll && newPostImage) {
        const uploadResponse = await uploadImage(newPostImage, "announcements");
        if (uploadResponse.success) {
          imageUrl = uploadResponse.url;
        }
      }

      // Upload video if present (skipped for polls)
      if (!hasPoll && newPostVideo) {
        const uploadResponse = await uploadVideo(newPostVideo, "announcements");
        if (uploadResponse.success) {
          videoUrl = uploadResponse.url;
        }
      }

      const pollPayload = pollValid
        ? {
            question: pollDraft.question.trim(),
            options: validPollOptions.map((text) => ({ text })),
          }
        : null;

      const response = await createAnnouncement(
        mintSpace._id,
        newPost.trim(),
        imageUrl,
        videoUrl,
        null,
        type,
        pollPayload
      );

      if (response.success) {
        setPosts([response.data, ...posts]);
        setNewPost("");
        setNewPostImage(null);
        setNewPostImagePreview(null);
        setNewPostVideo(null);
        setNewPostVideoPreview(null);
        setPollDraft(null);
        setToast(t(postedLabel) || (isPost ? "Post shared" : "Announcement posted"));
      }
    } catch (error) {
      console.error("Error posting announcement:", error);
      setToast(error.message || "Failed to post announcement");
    } finally {
      setUploading(false);
    }
  };

  const handleLike = async (postId) => {
    // Optimistic update — flip the like immediately, reconcile with server, revert on error
    const snapshot = posts;
    setPosts((prev) =>
      prev.map((post) => {
        if (post._id !== postId) return post;
        const likes = post.likes || [];
        const liked = likes.some((l) => {
          const id = typeof l === "string" ? l : l?._id || l;
          return id?.toString() === user?.id;
        });
        const nextLikes = liked
          ? likes.filter((l) => {
              const id = typeof l === "string" ? l : l?._id || l;
              return id?.toString() !== user?.id;
            })
          : [...likes, user?.id];
        return { ...post, likes: nextLikes };
      })
    );
    try {
      const response = await toggleAnnouncementLike(postId, mintSpace?._id);
      if (response.success) {
        setPosts((prev) =>
          prev.map((post) => (post._id === postId ? response.data : post))
        );
      }
    } catch (error) {
      console.error("Error toggling like:", error);
      setPosts(snapshot);
      setToast(t("toasts.failedUpdateLike"));
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      const response = await deleteAnnouncement(deleteTarget, mintSpace?._id);
      if (response.success) {
        setPosts((prev) => prev.filter((post) => post._id !== deleteTarget));
        setToast(t("toasts.announcementDeleted"));
        setDeleteTarget(null);
      }
    } catch (error) {
      console.error("Error deleting announcement:", error);
      setToast(t("toasts.failedDeleteAnnouncement"));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleShare = async (post) => {
    const mintSpaceId = mintSpace?._id;
    if (!mintSpaceId) {
      setToast(t("common.copyFailed") || "Cannot share this post");
      return;
    }
    const url = `${window.location.origin}/mint-space/${mintSpaceId}?nav=home&post=${post._id}`;
    const title = getAuthorName(post.author);
    const text = post.content
      ? post.content.slice(0, 200)
      : post.poll?.question || "Shared post";

    if (navigator.share) {
      try {
        await navigator.share({ title, text, url });
        return;
      } catch (e) {
        if (e?.name === "AbortError") return; // user cancelled the share sheet
      }
    }

    try {
      await navigator.clipboard.writeText(url);
      setToast(t("common.linkCopied") || "Link copied to clipboard");
    } catch {
      setToast(t("common.copyFailed") || "Could not copy link");
    }
  };

  const handleVote = async (postId, optionIndex) => {
    try {
      const response = await voteOnAnnouncementPoll(postId, optionIndex, mintSpace?._id);
      if (response.success) {
        setPosts((prev) => prev.map((p) => (p._id === postId ? response.data : p)));
      }
    } catch (error) {
      console.error("Error voting:", error);
      setToast(error?.message || "Failed to vote");
    }
  };

  const toggleComments = (postId) => {
    setOpenComments((prev) => ({ ...prev, [postId]: !prev[postId] }));
  };

  const replacePost = (next) => setPosts((prev) => prev.map((p) => (p._id === next._id ? next : p)));

  // Poll helpers
  const userVotedIndex = (post) => {
    if (!post.poll || !user) return -1;
    return post.poll.options.findIndex((opt) =>
      (opt.votes || []).some((v) => {
        const vid = typeof v === "string" ? v : v?._id || v;
        return vid?.toString() === user.id;
      })
    );
  };
  const pollTotalVotes = (post) =>
    post.poll ? post.poll.options.reduce((sum, o) => sum + (o.votes?.length || 0), 0) : 0;

  // Check if user liked a post
  const isLiked = (post) => {
    if (!user || !post.likes) return false;
    return post.likes.some((likeId) => {
      const likeUserId = typeof likeId === 'string' ? likeId : (likeId._id || likeId);
      return likeUserId.toString() === user.id;
    });
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? "s" : ""} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
    return date.toLocaleDateString();
  };

  // Get author name
  const getAuthorName = (author) => {
    if (typeof author === 'string') return author;
    return author.username || author.businessName || "Unknown";
  };

  // Get author avatar
  const getAuthorAvatar = (author) => {
    if (typeof author === 'string') return null;
    return author.profile?.avatarImage || author.profile?.avatar || null;
  };

  const baseText = "text-label-primary";
  const mutedText = "text-label-secondary";
  const cardBase = "border-separator-default bg-bg-raised";
  const inputBase = "border-transparent bg-fill-tertiary text-label-primary placeholder:text-label-tertiary focus:ring-2 focus:ring-focus-ring";

  const owner = typeof mintSpace?.owner === "object" ? mintSpace.owner : null;
  const ownerName = owner?.username || owner?.businessName || "";
  const memberCount = mintSpace?.memberCount ?? (mintSpace?.members?.length || 0) + 1;
  const bannerUrl = bannerOverride || mintSpace?.pages?.welcome?.banner || "";
  const logoUrl = logoOverride || mintSpace?.logo || "";
  const spaceInitial = (mintSpace?.name || "M").charAt(0).toUpperCase();
  const canEditLogo = !!isOwner;

  return (
    <div className="flex w-full flex-col gap-space-24 p-space-8 lg:p-0 lg:pb-space-24">
      {isPost && mintSpace && (
        <div className="overflow-hidden rounded-radius-lg bg-bg-raised">
          {/* Cover banner */}
          <div
            onClick={() => {
              if (!canEditLogo || bannerUploading) return;
              bannerInputRef.current?.click();
            }}
            className={`group relative h-[100px] w-full bg-fill-quaternary min-[600px]:h-[240px] ${canEditLogo ? "cursor-pointer" : ""}`}
          >
            {bannerUrl && (
              <img src={bannerUrl} alt="" className="absolute inset-0 h-full w-full object-cover" />
            )}
            {canEditLogo && (
              <input
                ref={bannerInputRef}
                type="file"
                accept="image/*"
                onChange={handleBannerChange}
                disabled={bannerUploading}
                className="hidden"
                aria-hidden
              />
            )}
            {canEditLogo && !bannerUploading && (
              <div className="absolute top-3 right-3 opacity-0 transition group-hover:opacity-100">
                <span className="inline-flex items-center gap-1.5 rounded-radius-xs bg-black/55 px-3 py-1.5 text-footnote font-medium text-white shadow">
                  <FiImage className="h-3.5 w-3.5" />
                  {t("mintSpace.changeBanner") || "Change banner"}
                </span>
              </div>
            )}
            {bannerUploading && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                <FiLoader className="h-8 w-8 text-accent-text animate-spin" />
              </div>
            )}
          </div>
          {/* Figma "Space hero", as on a profile: logo, name, what it is, its rating, bio and links. */}
          <div className="flex flex-wrap gap-space-16 px-space-24 py-space-16">
            {canEditLogo && (
              <input
                ref={logoInputRef}
                type="file"
                accept="image/*"
                onChange={handleLogoChange}
                disabled={logoUploading}
                className="hidden"
                aria-hidden
              />
            )}
            <div
              onClick={() => {
                if (!canEditLogo || logoUploading) return;
                logoInputRef.current?.click();
              }}
              className={`group relative flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-14 bg-fill-secondary min-[600px]:size-[64px] min-[600px]:rounded-[20px] ${
                canEditLogo ? "cursor-pointer" : ""
              }`}
            >
              {logoUrl ? (
                <img src={logoUrl} alt={mintSpace.name} className="h-full w-full object-cover" />
              ) : (
                <span className="text-headline text-label-secondary">{spaceInitial}</span>
              )}
              {canEditLogo && !logoUploading && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition group-hover:bg-black/45 group-hover:opacity-100">
                  <span className="rounded-md bg-black/60 px-2 py-0.5 text-caption-1 font-medium text-white shadow">
                    {t("mintSpace.changeBanner") || "Change image"}
                  </span>
                </div>
              )}
              {logoUploading && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                  <FiLoader className="h-5 w-5 animate-spin text-accent-text" />
                </div>
              )}
            </div>
            <div className="flex min-w-0 flex-1 basis-[200px] flex-col gap-space-12">
              <div className="flex flex-col gap-space-4">
                <h1 className="break-words text-title-2 text-label-primary">{mintSpace.name}</h1>
                <p className="text-footnote text-label-secondary">
                  {[
                    mintSpace.category && getBusinessCategoryLabel(t, mintSpace.category),
                    t(memberCount === 1 ? "home.membersOne" : "home.membersOther", { count: memberCount }),
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
                <ReviewsRow average={rating.averageRating} count={rating.ratingCount} language={language} t={t} />
                {(mintSpace.bio || isOwner) && (
                  <p className="whitespace-pre-line break-words text-footnote text-label-tertiary">
                    {mintSpace.bio || t("profile.noBioYet")}
                  </p>
                )}
              </div>
              <SpaceSocialLinks
                mintSpace={mintSpace}
                canEdit={isOwner}
                isDark={isDark}
                setToast={setToast}
                onUpdate={onUpdate}
              />
            </div>
            {/* As on the sidebar's cover: share the space, and open its owner's profile. */}
            <div className="flex flex-wrap items-center gap-space-8 self-start">
              <IconButton
                size="m"
                label={t("common.share")}
                onClick={() => shareOrCopyUrl(getShareUrl(`/mint-space/${mintSpace._id}`), setToast, t)}
              >
                <Icon name="share" size={20} />
              </IconButton>
              {owner?._id && String(owner._id) !== String(user?._id || user?.id) && (
                <Button
                  variant="gray"
                  size="m"
                  className="max-w-[220px]"
                  icon={<Avatar size="s" src={getProfileAvatarUrl(owner)} name={ownerName} />}
                  onClick={() => navigate(`/profile/${owner._id}`)}
                >
                  <span className="truncate normal-case">{ownerName}</span>
                </Button>
              )}
              {isOwner && onEditSpace && (
                <Button variant="gray" size="m" icon={<Icon name="edit" size={20} />} onClick={onEditSpace}>
                  {t("mintSpace.editSpace")}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Products — between the name/banner header and the post feed */}
      {isPost && <MintSpaceProductsGrid mintSpace={mintSpace} isDark={isDark} />}

      <div className={isPost ? "grid grid-cols-1 items-start gap-space-24 xl:grid-cols-[1fr_320px]" : ""}>
      <div className="flex min-w-0 flex-col gap-space-24">
        {!isPost && <h2 className="text-title-3 text-label-primary">{pageTitle}</h2>}

        {/* Create Post - Only show for owner */}
        {isOwner && (
          <div className="flex flex-col gap-space-12 rounded-radius-lg bg-bg-raised p-space-16">
          {isPost && (
            <p className="inline-flex h-[40px] max-w-full items-center self-start rounded-[22px] bg-fill-secondary px-[14px] text-subheadline-semibold text-label-primary">
              <span className="truncate">{mintSpace?.name}</span>
            </p>
          )}
          <div className="flex gap-space-12">
            <Avatar size="m" src={getProfileAvatarUrl(user)} name={user?.username || user?.businessName || "U"} />
            <textarea
              value={newPost}
              onChange={(e) => setNewPost(e.target.value)}
              placeholder={t("actualHome.whatsOnYourMind")}
              rows={1}
              className="min-h-[64px] min-w-0 flex-1 resize-none rounded-[22px] bg-fill-quaternary p-space-16 text-body text-label-primary outline-none placeholder:text-label-tertiary focus-visible:shadow-[0_0_0_2px_var(--color-focus-ring)]"
          />
          </div>

          {newPostImagePreview && (
            <div className="relative mb-3 rounded-radius-sm overflow-hidden">
              <img
                src={newPostImagePreview}
                alt="Post preview"
                className="w-full max-h-64 object-cover"
              />
              <button
                onClick={() => {
                  setNewPostImage(null);
                  setNewPostImagePreview(null);
                }}
                className={`absolute top-2 right-2 p-1.5 rounded-radius-xs backdrop-blur-sm transition ${
                  isDark
                    ? "bg-black/50 hover:bg-black/70 text-white"
                    : "bg-white/80 hover:bg-white text-slate-900"
                }`}
              >
                <FiTrash2 className="h-4 w-4" />
              </button>
            </div>
          )}

          {newPostVideoPreview && (
            <div className="relative mb-3 rounded-radius-sm overflow-hidden">
              <CustomVideoPlayer
                src={newPostVideoPreview}
                isDark={isDark}
                className="max-h-96"
              />
              <button
                onClick={() => {
                  setNewPostVideo(null);
                  setNewPostVideoPreview(null);
                }}
                className={`absolute top-2 right-2 p-1.5 rounded-radius-xs backdrop-blur-sm transition z-10 ${
                  isDark
                    ? "bg-black/50 hover:bg-black/70 text-white"
                    : "bg-white/80 hover:bg-white text-slate-900"
                }`}
              >
                <FiTrash2 className="h-4 w-4" />
              </button>
            </div>
          )}

          {pollDraft && (
            <div
              className={`relative mb-3 overflow-hidden rounded-radius-md border ${
                isDark ? "border-accent-default bg-mint/[0.04]" : "border-accent-default bg-mint/[0.03]"
              }`}
            >
              {/* Header */}
              <div
                className={`flex items-center justify-between gap-2 px-4 py-3 border-b ${
                  "border-separator-default bg-bg-raised"
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className="flex h-8 w-8 items-center justify-center rounded-radius-xs bg-accent-default text-accent-on">
                    <FiBarChart2 className="h-4 w-4" />
                  </span>
                  <span className={`text-subheadline-semibold ${baseText}`}>
                    {t("campaigns.poll") || "Poll"}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setPollDraft(null)}
                  className={`flex h-7 w-7 items-center justify-center rounded-radius-xs transition ${
                    isDark ? "text-slate-400 hover:bg-white/10 hover:text-status-danger" : "text-slate-400 hover:bg-slate-200 hover:text-status-danger"
                  }`}
                  aria-label="Remove poll"
                >
                  <FiX className="h-4 w-4" />
                </button>
              </div>

              <div className="p-4 space-y-3">
                <input
                  value={pollDraft.question}
                  onChange={(e) =>
                    setPollDraft((p) => ({ ...p, question: e.target.value }))
                  }
                  placeholder={t("campaigns.pollQuestionPlaceholder") || "Ask a question…"}
                  maxLength={280}
                  className={`w-full px-4 py-3 rounded-radius-sm border text-headline ${inputBase} focus:outline-none focus:ring-2 focus:ring-accent-default`}
                />

                <div className="space-y-2">
                  {pollDraft.options.map((opt, idx) => (
                    <div key={idx} className="group flex items-center gap-2.5">
                      <span
                        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-footnote-semibold ${
                          "bg-fill-tertiary text-label-secondary"
                        }`}
                      >
                        {idx + 1}
                      </span>
                      <input
                        value={opt}
                        onChange={(e) =>
                          setPollDraft((p) => {
                            const next = [...p.options];
                            next[idx] = e.target.value;
                            return { ...p, options: next };
                          })
                        }
                        placeholder={`${t("campaigns.option") || "Option"} ${idx + 1}`}
                        maxLength={120}
                        className={`flex-1 px-3.5 py-2.5 rounded-radius-sm border ${inputBase} focus:outline-none focus:ring-2 focus:ring-accent-default`}
                      />
                      {pollDraft.options.length > 2 && (
                        <button
                          type="button"
                          onClick={() =>
                            setPollDraft((p) => ({
                              ...p,
                              options: p.options.filter((_, i) => i !== idx),
                            }))
                          }
                          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-radius-xs transition ${
                            "text-label-tertiary hover:bg-fill-quaternary hover:text-status-danger"
                          }`}
                          aria-label="Remove option"
                        >
                          <FiX className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                {pollDraft.options.length < 10 ? (
                  <button
                    type="button"
                    onClick={() =>
                      setPollDraft((p) => ({ ...p, options: [...p.options, ""] }))
                    }
                    className={`flex w-full items-center justify-center gap-1.5 rounded-radius-sm border border-dashed py-2.5 text-subheadline-semibold transition ${
                      isDark
                        ? "border-white/15 text-slate-300 hover:border-accent-default hover:text-accent-text"
                        : "border-slate-300 text-slate-500 hover:border-accent-default hover:text-accent-text"
                    }`}
                  >
                    <FiPlus className="h-4 w-4" />
                    {t("campaigns.addOption") || "Add option"}
                  </button>
                ) : (
                  <p className={`text-center text-footnote ${mutedText}`}>
                    {t("campaigns.maxOptionsReached") || "Maximum of 10 options"}
                  </p>
                )}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-space-4">
              <label className={`flex size-8 cursor-pointer items-center justify-center rounded-full text-label-secondary transition-colors hover:bg-fill-quaternary ${pollDraft ? "pointer-events-none opacity-40" : ""}`}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                  disabled={!!pollDraft}
                />
                <Icon name="image" size={18} />
              </label>
              <label className={`flex size-8 cursor-pointer items-center justify-center rounded-full text-label-secondary transition-colors hover:bg-fill-quaternary ${pollDraft ? "pointer-events-none opacity-40" : ""}`}>
                <input
                  type="file"
                  accept="video/*"
                  onChange={handleVideoChange}
                  className="hidden"
                  disabled={!!pollDraft}
                />
                <Icon name="video" size={18} />
              </label>
              <button
                type="button"
                onClick={() => {
                  if (pollDraft) {
                    setPollDraft(null);
                  } else {
                    setNewPostImage(null);
                    setNewPostImagePreview(null);
                    setNewPostVideo(null);
                    setNewPostVideoPreview(null);
                    setPollDraft({ question: "", options: ["", ""] });
                  }
                }}
                aria-label="Add poll"
                className={`flex size-8 items-center justify-center rounded-full transition-colors hover:bg-fill-quaternary ${
                  pollDraft ? "text-accent-text" : mutedText
                }`}
              >
                <Icon name="chart" size={18} />
              </button>
            </div>
            <Button size="s" icon={<Icon name="send" size={16} />} onClick={handlePost} loading={uploading}>
              {uploading ? "Posting..." : "Post"}
            </Button>
          </div>
        </div>
        )}

        {/* Posts List */}
        <div className="flex flex-col gap-space-24">
          {loading ? (
            <p className="text-subheadline text-label-secondary">{loadingLabel}</p>
          ) : (
            <>
              {posts.map((post) => {
                const authorName = getAuthorName(post.author);
                const authorAvatar = getAuthorAvatar(post.author);
                const authorId = typeof post.author === "string" ? post.author : post.author._id || post.author;
                const firstLink = findLinks(post.content)[0];
                const liked = isLiked(post);
                const canDelete = isOwner && (post.author._id?.toString() === user?.id || post.author.toString() === user?.id);

                return (
                  <div
                    key={post._id}
                    id={`post-${post._id}`}
                    className={`rounded-radius-lg bg-bg-raised-end p-space-16 transition-shadow duration-500 ${
                      highlightedPostId === post._id ? "ring-2 ring-accent-default" : ""
                    }`}
                  >
                    {/* Post header: the author's picture beside their name, as on the main feed */}
                    <div className="flex items-center gap-space-12">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/profile/${authorId}`);
                        }}
                        className="shrink-0 rounded-14"
                        aria-label={authorName}
                      >
                        <Avatar size="m" src={authorAvatar} name={authorName} />
                      </button>
                      <div className="flex min-w-0 flex-1 flex-col gap-space-4">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/profile/${authorId}`);
                          }}
                          className="truncate text-left text-subheadline-semibold text-label-primary hover:underline"
                        >
                          {authorName}
                        </button>
                        <div className="flex flex-wrap items-center gap-x-[6px] text-footnote text-label-secondary">
                          {mintSpace?._id && (
                            <>
                              <span className="inline-flex min-w-0 items-center gap-[6px]">
                                <Avatar src={mintSpace.logo} name={mintSpace.name} />
                                <span className="truncate">{mintSpace.name}</span>
                              </span>
                              <span aria-hidden>·</span>
                            </>
                          )}
                          <time>{formatTimestamp(post.createdAt)}</time>
                        </div>
                      </div>
                      {canDelete && (
                        <IconButton variant="plain" label={t("common.delete")} onClick={() => setDeleteTarget(post._id)}>
                          <Icon name="trash" size={18} />
                        </IconButton>
                      )}
                    </div>

                    {/* Post body */}
                    <div className="mt-space-12 flex flex-col gap-space-12">
                      {post.content && (
                        <LinkifiedText text={post.content} className="whitespace-pre-wrap break-words text-body text-label-primary" />
                      )}
                      {firstLink && !post.image && !post.video && <LinkPreviewCard url={firstLink} />}
                      {post.campaign && (
                        <div>
                          <CampaignAnnouncementCard
                            campaign={post.campaign}
                            isDark={isDark}
                            mintSpaceId={mintSpace?._id}
                          />
                        </div>
                      )}
                      {post.image && (
                        <img
                          src={post.image}
                          alt="Post"
                          className="max-h-96 w-full rounded-radius-md object-cover"
                        />
                      )}
                      {post.video && (
                        <div>
                          <CustomVideoPlayer
                            src={post.video}
                            isDark={isDark}
                            className="max-h-96"
                          />
                        </div>
                      )}
                      {post.poll && post.poll.options?.length > 0 && (() => {
                        const total = pollTotalVotes(post);
                        const votedIdx = userVotedIndex(post);
                        return (
                          <div className="space-y-2">
                            {post.poll.question && (
                              <p className={`font-semibold ${baseText}`}>
                                {post.poll.question}
                              </p>
                            )}
                            {post.poll.options.map((opt, idx) => {
                              const count = opt.votes?.length || 0;
                              const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                              const isMyVote = votedIdx === idx;
                              return (
                                <button
                                  key={idx}
                                  type="button"
                                  onClick={() => handleVote(post._id, idx)}
                                  className={`relative w-full overflow-hidden rounded-radius-sm border px-4 py-2.5 text-left transition ${
                                    "border-separator-default bg-bg-raised hover:border-accent-default"
                                  } ${isMyVote ? "ring-2 ring-accent-default" : ""}`}
                                >
                                  <div
                                    className={`absolute inset-y-0 left-0 ${
                                      isMyVote
                                        ? "bg-accent-tinted"
                                        : "bg-fill-tertiary"
                                    }`}
                                    style={{ width: `${pct}%` }}
                                    aria-hidden
                                  />
                                  <div className="relative flex items-center justify-between gap-3">
                                    <span className={`font-medium ${baseText}`}>{opt.text}</span>
                                    <span className={`text-footnote-semibold tabular-nums ${mutedText}`}>
                                      {pct}% · {count}
                                    </span>
                                  </div>
                                </button>
                              );
                            })}
                            <p className={`text-footnote ${mutedText}`}>
                              {total} {total === 1 ? "vote" : "votes"}
                              {votedIdx >= 0 ? " · click another option to change your vote" : ""}
                            </p>
                          </div>
                        );
                      })()}
                    </div>

                    {/* Actions */}
                    <div className="mt-space-12 flex items-center gap-space-24">
                      <button
                        type="button"
                        onClick={() => handleLike(post._id)}
                        aria-pressed={liked}
                        className={`flex items-center gap-space-8 text-footnote transition-colors hover:text-label-primary ${liked ? "text-status-danger" : "text-label-secondary"}`}
                      >
                        <Icon name="heart" size={18} className={liked ? "fill-current" : ""} />
                        <span>{post.likes?.length || 0}</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleComments(post._id)}
                        aria-expanded={Boolean(openComments[post._id])}
                        className={`flex items-center gap-space-8 text-footnote transition-colors hover:text-label-primary ${openComments[post._id] ? "text-accent-text" : "text-label-secondary"}`}
                      >
                        <Icon name="message" size={18} />
                        <span>{post.comments?.length || 0}</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleShare(post)}
                        aria-label={t("actualHome.share")}
                        className="flex items-center gap-space-8 text-footnote text-label-secondary transition-colors hover:text-label-primary"
                      >
                        <Icon name="share" size={18} />
                      </button>
                    </div>

                    {openComments[post._id] && (
                      <div className="mt-space-12 border-t border-separator-default pt-space-12">
                        <PostComments
                          post={post}
                          isDark={isDark}
                          canPin={!!isOwner}
                          mintSpaceId={mintSpace?._id}
                          onPostChange={replacePost}
                          setToast={setToast}
                        />
                      </div>
                    )}
                  </div>
                );
              })}

              {posts.length === 0 && (
                <EmptyState icon="message" title={emptyLabel} />
              )}
            </>
          )}
        </div>
      </div>
      {isPost && mintSpace?._id && (
        <aside className="xl:sticky xl:top-0">
          <CampaignPeopleCard
            mintSpaceId={mintSpace._id}
            isDark={isDark}
            setToast={setToast}
            onSeeAll={onNavigate ? () => onNavigate("members") : undefined}
          />
        </aside>
      )}
      </div>

      <Alert
        open={!!deleteTarget}
        title={t("toasts.deletePostTitle")}
        message={t("toasts.deletePostBody")}
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        onConfirm={confirmDelete}
        onClose={() => setDeleteTarget(null)}
        loading={isDeleting}
        isDark={isDark}
      />
    </div>
  );
};

export default MintSpaceAnnouncementsPage;




